import ky, { type KyInstance } from 'ky';

/**
 * Configuration for the shared API client.
 *
 * Each consuming app calls {@link configureApiClient} once (at module scope, before
 * any request is made) to point the client at its own API. If nothing is configured
 * the client falls back to the environment variables below, which keeps the existing
 * Sizzle frontend working without changes.
 */
export interface ApiClientConfig {
  /** Absolute base URL for the API, e.g. `https://api.sizzle.nz/`. A trailing slash is added if missing. */
  baseUrl?: string;
  /**
   * Called on every client construction to resolve the base URL. Use this when the
   * URL depends on runtime information (e.g. deriving the host from `window.location`).
   * Takes precedence over `baseUrl`.
   */
  resolveBaseUrl?: () => string;
  /** Request timeout in milliseconds. Defaults to 30000. */
  timeout?: number;
}

let config: ApiClientConfig = {};

/** Resolved clients are cached; configuring resets them so the next call picks up new options. */
let cachedApiClient: KyInstance | null = null;
let cachedAuthenticatedApiClient: KyInstance | null = null;

/**
 * Set the API client configuration. Call this once during app bootstrap, before
 * any request is issued. Safe to call multiple times — cached clients are rebuilt.
 */
export function configureApiClient(next: ApiClientConfig): void {
  config = { ...config, ...next };
  cachedApiClient = null;
  cachedAuthenticatedApiClient = null;
}

function withTrailingSlash(url: string | undefined): string {
  if (!url) return url as string;
  return url.endsWith('/') ? url : `${url}/`;
}

/**
 * Resolve the API base URL.
 *
 * Precedence: `resolveBaseUrl()` > `baseUrl` > environment variables.
 * The environment fallback mirrors the original Sizzle frontend behaviour, including
 * the `SIZZLE_API_USE_HOSTNAME` dev escape hatch that derives the API host from the
 * current browser location.
 */
export function getApiBaseUrl(): string {
  if (config.resolveBaseUrl) {
    return withTrailingSlash(config.resolveBaseUrl());
  }
  if (config.baseUrl) {
    return withTrailingSlash(config.baseUrl);
  }

  let baseUrl: string | undefined;
  if (typeof window === 'undefined') {
    // SSR: only server-visible vars are available.
    baseUrl = process.env.SIZZLE_API_ENDPOINT || process.env.NEXT_PUBLIC_API_URL;
  } else if (process.env.SIZZLE_API_USE_HOSTNAME === 'true') {
    const { hostname, protocol } = window.location;
    baseUrl = `${protocol}//${hostname}:3010/`;
  } else {
    baseUrl = process.env.SIZZLE_API_ENDPOINT || process.env.NEXT_PUBLIC_API_URL;
  }

  return withTrailingSlash(baseUrl);
}

function createApiClient(): KyInstance {
  return ky.create({
    prefixUrl: getApiBaseUrl(),
    headers: {
      'Content-Type': 'application/json',
    },
    retry: {
      limit: 1,
      methods: ['get', 'put', 'patch', 'delete'],
      statusCodes: [408, 413, 429, 500, 502, 503, 504],
    },
    timeout: config.timeout ?? 30000,
    credentials: 'include',
    hooks: {
      beforeError: [
        async (error) => {
          // Parse the response body and attach API error details to the error object.
          // This ensures message/column/code are available even after the body stream is consumed.
          try {
            const body = await error.response.json();
            if ((body as any)?.error) {
              if ((body as any).error.message) {
                error.message = (body as any).error.message;
              }
              // Attach field-level error info for form error handling
              (error as any).code = (body as any).error.code;
              (error as any).column = (body as any).error.column;
            }
          } catch {
            // Response body not JSON or already consumed
          }
          return error;
        },
      ],
    },
  });
}

function createAuthenticatedApiClient(): KyInstance {
  return createApiClient().extend({
    hooks: {
      beforeRequest: [
        (request: Request) => {
          // Add authentication headers when available
          const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        },
      ],
    },
  });
}

/**
 * Wraps a ky factory in a lazy proxy so the instance (and therefore the resolved
 * base URL) is created on first use rather than at module import time. Without this,
 * `configureApiClient` would have to run before this module is even imported.
 */
function lazyKyInstance(factory: () => KyInstance, cache: 'base' | 'authenticated'): KyInstance {
  const resolve = (): KyInstance => {
    if (cache === 'base') {
      return (cachedApiClient ??= factory());
    }
    return (cachedAuthenticatedApiClient ??= factory());
  };

  // The proxy target must be callable because ky instances are callable (`ky(url, opts)`).
  const callableTarget = function proxyTarget(): void {} as unknown as KyInstance;

  return new Proxy(callableTarget, {
    get: (_target, prop, receiver) => Reflect.get(resolve() as object, prop, receiver),
    has: (_target, prop) => prop in (resolve() as object),
    apply: (_target, _thisArg, args) => (resolve() as any)(...args),
  }) as KyInstance;
}

/** Base API client. Sends cookies, does not attach a bearer token. */
export const apiClient: KyInstance = lazyKyInstance(createApiClient, 'base');

/** API client that attaches a bearer token from localStorage when one is present. */
export const authenticatedApiClient: KyInstance = lazyKyInstance(
  createAuthenticatedApiClient,
  'authenticated'
);

/** Helper function to create authenticated requests */
export function createAuthenticatedRequest(): KyInstance {
  return authenticatedApiClient;
}
